/* =========================================================================
   estate-sso.ts — single-login handoff between Stratosfyre estate apps.

   Every estate app exposes a public `/auth/callback` surface that consumes
   a session hash of the form:

     {origin}/auth/callback#access_token=...&refresh_token=...&expires_in=...
       &expires_at=...&token_type=bearer&next=%2Fsome%2Fpath

   `buildEstateHandoffUrl` produces that URL from the live Supabase session
   and `openEstateApp` performs SAME-TAB navigation (the estate should feel
   like one platform), falling back to the plain app URL when no session
   exists. Tokens only ever travel in the URL FRAGMENT (never query/path),
   and each consumer strips them from history immediately after setSession.
   ========================================================================= */

import { supabase } from "./supabase-client";
import type { Session } from "@supabase/supabase-js";

export type EstateAppKey =
  | "website"
  | "ascent"
  | "portfolio_pulse"
  | "inspection_flow"
  | "pitch_studio"
  | "archai_studio"
  | "campus_lease_studio";

export interface EstateAppEntry {
  /** Human-readable display name. */
  name: string;
  /** Production deployment. */
  prodUrl: string;
  /** Local development URL (port matches the estate dev-port map). */
  devUrl: string;
  /** Per-deployment override env var (matches estate-links conventions). */
  envVar: string;
}

/** Estate module registry — key is the `module_access.module` slug. */
export const ESTATE_APPS: Record<EstateAppKey, EstateAppEntry> = {
  website: {
    name: "Stratosfyre Website",
    prodUrl: "https://stratosfyre-website.vercel.app",
    devUrl: "http://localhost:8080",
    envVar: "VITE_ESTATE_WEBSITE_URL",
  },
  ascent: {
    name: "Stratosfyre Ascent",
    prodUrl: "https://stratosfyre-ascent.vercel.app",
    devUrl: "http://localhost:5173",
    envVar: "VITE_ESTATE_ASCENT_URL",
  },
  portfolio_pulse: {
    name: "Portfolio Pulse",
    prodUrl: "https://syd-portfolio-pulse.vercel.app",
    devUrl: "http://localhost:8081",
    envVar: "VITE_ESTATE_PORTFOLIO_PULSE_URL",
  },
  inspection_flow: {
    name: "Inspection Flow",
    prodUrl: "https://inspection-flow-eosin.vercel.app",
    devUrl: "http://localhost:8082",
    envVar: "VITE_ESTATE_INSPECTION_FLOW_URL",
  },
  pitch_studio: {
    name: "Pitch Studio",
    prodUrl: "https://stratosfyre-pitch-studio.vercel.app",
    devUrl: "http://localhost:8083",
    envVar: "VITE_ESTATE_PITCH_STUDIO_URL",
  },
  archai_studio: {
    name: "Archai Studio",
    // Archai is deployed on Render, not Vercel. `archai-studio.vercel.app` is
    // an UNCLAIMED subdomain squatted by a third party — never point a token
    // handoff at it (see ESTATE_TOKEN_ORIGIN_ALLOWLIST below).
    prodUrl: "https://archai-studio.onrender.com",
    devUrl: "http://localhost:5174",
    envVar: "VITE_ESTATE_ARCHAI_URL",
  },
  campus_lease_studio: {
    name: "TENURE",
    prodUrl: "https://stratosfyre-client-files.pages.dev",
    devUrl: "http://localhost:5175",
    envVar: "VITE_ESTATE_CAMPUS_LEASE_URL",
  },
};

/** This app's `module_access` slug. */
export const THIS_MODULE: EstateAppKey = "campus_lease_studio";

const env = import.meta.env as Record<string, string | undefined>;

const isLocalHostname = (hostname: string) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "[::1]" ||
  hostname.endsWith(".local");

const stripTrailingSlash = (url: string) => url.replace(/\/+$/, "");

/**
 * Resolve the base URL for an estate app: env override → local-dev port map
 * (same LAN-friendly hostname logic as estate-links) → prod domain.
 */
export function getEstateAppUrl(appKey: EstateAppKey): string {
  const entry = ESTATE_APPS[appKey];
  const override = env[entry.envVar];
  if (override) return stripTrailingSlash(override);

  if (typeof globalThis !== "undefined" && globalThis.location) {
    const { hostname, protocol } = globalThis.location;
    if (isLocalHostname(hostname)) {
      const devPort = new URL(entry.devUrl).port;
      return `${protocol}//${hostname}:${devPort}`;
    }
  }

  return entry.prodUrl;
}

/**
 * Build the cross-origin handoff URL that carries the current session to a
 * sibling estate app via the URL fragment.
 */
export function buildEstateHandoffUrl(
  appKey: EstateAppKey,
  session: Session,
  next: string = "/",
): string {
  const base = getEstateAppUrl(appKey);
  const params = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: String(session.expires_in ?? ""),
    expires_at: String(session.expires_at ?? ""),
    token_type: "bearer",
    next,
  });
  return `${base}/auth/callback#${params.toString()}`;
}

/**
 * Origins allowed to RECEIVE a token handoff. The registry above answers
 * "where does this app live"; handing over a live session requires the
 * origin here too — a second, security-reviewed key. Each origin below is a
 * deployment we control on the shared Supabase issuer.
 * (Ported from the estate-canonical copy — syd-portfolio-pulse #153.)
 */
export const ESTATE_TOKEN_ORIGIN_ALLOWLIST: ReadonlySet<string> = new Set([
  "https://stratosfyre-website.vercel.app",
  "https://stratosfyre-ascent.vercel.app",
  "https://syd-portfolio-pulse.vercel.app",
  "https://inspection-flow-eosin.vercel.app",
  "https://stratosfyre-pitch-studio.vercel.app",
  "https://archai-studio.onrender.com",
  "https://stratosfyre-client-files.pages.dev",
]);

/**
 * True only when `baseUrl`'s origin is an allowlisted estate deployment, or a
 * local dev host (the operator's own machine, not a hijack surface). Anything
 * else — including any origin reached only through a registry `prodUrl` that
 * is not also listed above — must be navigated to WITHOUT a token handoff.
 */
export function isEstateTokenHandoffAllowed(baseUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return false;
  }
  if (ESTATE_TOKEN_ORIGIN_ALLOWLIST.has(url.origin)) return true;
  return url.protocol === "http:" && isLocalHostname(url.hostname);
}

/**
 * Same-tab navigation seam. jsdom's `window.location` is unforgeable and
 * cannot be stubbed, so tests spy on this object instead.
 */
export const estateNavigation = {
  assign(url: string) {
    globalThis.location.assign(url);
  },
};

/**
 * Open a sibling estate app in the SAME tab. If a live Supabase session
 * exists it travels via the `/auth/callback` hash handoff; otherwise we
 * navigate to the plain app URL and let the target app's own auth take over.
 */
export async function openEstateApp(
  appKey: EstateAppKey,
  next: string = "/",
): Promise<void> {
  const base = getEstateAppUrl(appKey);
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (
      session?.access_token &&
      session?.refresh_token &&
      isEstateTokenHandoffAllowed(base)
    ) {
      estateNavigation.assign(buildEstateHandoffUrl(appKey, session, next));
      return;
    }
  } catch (err) {
    console.warn(
      "[estate-sso] getSession failed — navigating without handoff",
      err,
    );
  }
  estateNavigation.assign(base);
}

/**
 * URL of the estate front door (Website) sign-in page, asking it to hand
 * the session back to this app after login.
 */
export function estateSignInUrl(): string {
  const website = getEstateAppUrl("website");
  const here = globalThis.location?.origin ?? getEstateAppUrl(THIS_MODULE);
  return `${website}/auth?next=${encodeURIComponent(here + "/")}`;
}
